import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { SnackbarProvider } from "notistack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getAccountCloudType } from "../../CloudProviderContext";
import AdditionalAddons from "./AddonsManagement/AdditionalAddons/AdditionalAddons";
import ComplianceAddons from "./AddonsManagement/ComplianceAddons/ComplianceAddons";
import UpdateAccount from "./UpdateAccount";
import ViewAccountList from "./ViewAccountList";
import UpdateIamRole from "./UpdateIamRole";
import { shouldFilterUsername } from "../../helpers/util_helper";
import { AccountDetailThemeProvider } from "./accountDetailTheme";
import { ACCOUNT_DETAIL_FONT_SX } from "./accountDetailTypography";

const TAB_INFO_ICON_SX = {
  fontSize: 14,
  color: "#f59e0b",
  ml: 0.5,
};

const TAB_ACTIVE_ICON_SX = {
  fontSize: 14,
  color: "#22c55e",
  ml: 0.5,
};

const EditAccountDialog = ({
  open,
  onClose,
  editAccount,
  accounts = [],
  complianceAddOns = [],
  addOns = [],
  externalId,
  updateData,
}) => {
  const isAzureAccount = getAccountCloudType(editAccount) === "AZURE";
  const [activeTab, setActiveTab] = useState(isAzureAccount ? "compliance" : "finops");
  // Survives Cloud Event / FinOps / Cost Optimization tab remounts (same unlock gate as Add Ons List).
  const [addonsWithBlueButtonClicked, setAddonsWithBlueButtonClicked] = useState(
    () => new Set()
  );

  const markBlueButtonClicked = (addonName) => {
    setAddonsWithBlueButtonClicked((prev) => new Set([...prev, addonName]));
  };

  useEffect(() => {
    if (!open) return;
    setActiveTab(isAzureAccount ? "compliance" : "finops");
    setAddonsWithBlueButtonClicked(new Set());
  }, [open, isAzureAccount, editAccount?.accountCode]);

  const filteredUsers = useMemo(() => {
    if (!editAccount?.listusr) return [];
    return editAccount.listusr.filter(
      (user) =>
        user.isActivated &&
        !shouldFilterUsername(user.userName)
    );
  }, [editAccount]);

  const tabs = isAzureAccount
    ? [
        {
          id: "compliance",
          label: "Compliance",
          info: "If you want to add new compliances to your account, contact support.cloudcatcher@cloudnovatech.com",
        },
        { id: "users", label: "Users" },
        { id: "resourceGroup", label: "Resource Group" },
      ]
    : [
        {
          id: "finops",
          label: "FinOps",
          info: "Run in us-east-1 Region",
        },
        { id: "costOptimization", label: "Cost Optimization" },
        {
          id: "cloudEvent",
          label: "Cloud Event",
          info: "Please contact support if you would like to use encryption for CloudTrail logs.",
        },
        {
          id: "compliance",
          label: "Compliance",
          info: "If you want to add new compliances to your account, contact support.cloudcatcher@cloudnovatech.com",
        },
        { id: "users", label: "Users" },
        { id: "resourceGroup", label: "Resource Group" },
        {
          id: "pairing",
          label: "IAM",
          info:
            editAccount?.status === "INACTIVE"
              ? "Activate this account by pairing the IAM role."
              : "Rotate the External ID or update the IAM role for this account.",
        },
      ];

  if (!editAccount) return null;

  const isAddonActive = (addonName) =>
    (addOns || []).some((addon) => addon.name === addonName && addon.enabled);

  const activeTabStatus = {
    finops: isAddonActive("Cost Insight"),
    costOptimization: isAddonActive("Cost Optimization"),
    cloudEvent: isAddonActive("Event Insight"),
    pairing: editAccount.status === "ACTIVE",
  };

  const accountNumberLabel = isAzureAccount
    ? "Subscription ID"
    : "Account Number";

  return (
    <AccountDetailThemeProvider>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: "#F98125",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.35rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Edit Account
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              color: "rgba(255,255,255,0.9)",
              fontSize: "0.875rem",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 2.5,
              rowGap: 0.5,
            }}
          >
            <Box component="span">
              {accountNumberLabel}:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {editAccount.accountNumber}
              </Box>
            </Box>
            <Box component="span">
              Account Name:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                {editAccount.accountName}
              </Box>
            </Box>
          </Typography>
        </Box>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "#FFFFFF",
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 1,
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          px: 3,
          pt: 1.5,
          pb: 0,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f3f4f6",
            borderRadius: "10px",
            px: 0.5,
            py: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              width: "100%",
              "& .MuiTabs-flexContainer": {
                width: "100%",
                alignItems: "center",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 40,
                minWidth: 0,
                flex: 1,
                maxWidth: "none",
                px: 0.5,
                fontSize: "0.8125rem",
                color: "#6b7280",
                fontWeight: 500,
                borderRadius: "8px",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#111827 !important",
                fontWeight: 700,
                backgroundColor: "#ffffff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#111827",
                height: 3,
                borderRadius: "2px 2px 0 0",
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: 0.25,
                    }}
                  >
                    {tab.label}
                    {tab.id === "users" ? null : tab.id in activeTabStatus &&
                      activeTabStatus[tab.id] ? (
                      <CheckCircleIcon
                        sx={TAB_ACTIVE_ICON_SX}
                        titleAccess="Active"
                      />
                    ) : tab.id in activeTabStatus || tab.info ? (
                      tab.info ? (
                        <Tooltip title={tab.info}>
                          <InfoOutlinedIcon
                            onClick={(e) => e.stopPropagation()}
                            sx={TAB_INFO_ICON_SX}
                          />
                        </Tooltip>
                      ) : (
                        <InfoOutlinedIcon
                          sx={TAB_INFO_ICON_SX}
                          titleAccess="Inactive"
                        />
                      )
                    ) : null}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          px: 3,
          py: 2.5,
          overflowY: "auto",
          ...ACCOUNT_DETAIL_FONT_SX,
        }}
      >
        <SnackbarProvider>
          {activeTab === "finops" && (
            <AdditionalAddons
              addonName="Cost Insight"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
            />
          )}
          {activeTab === "costOptimization" && (
            <AdditionalAddons
              addonName="Cost Optimization"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
            />
          )}
          {activeTab === "cloudEvent" && (
            <AdditionalAddons
              addonName="Event Insight"
              addOns={addOns}
              editAccount={editAccount}
              accounts={accounts}
              addonsWithBlueButtonClicked={addonsWithBlueButtonClicked}
              markBlueButtonClicked={markBlueButtonClicked}
            />
          )}
          {activeTab === "compliance" && (
            <ComplianceAddons
              complianceAddOns={complianceAddOns}
              editAccount={editAccount}
              isAzureAccount={isAzureAccount}
            />
          )}
          {activeTab === "users" && (
            <ViewAccountList
              embedded
              modal_static
              closeModal={onClose}
              accountCode={editAccount.accountCode}
              accountNumber={editAccount.accountNumber}
              userList={filteredUsers}
            />
          )}
          {activeTab === "resourceGroup" && (
            <UpdateAccount
              embedded
              selectedAccount={editAccount}
              closeModal={onClose}
              updateData={updateData}
            />
          )}
          {activeTab === "pairing" && (
            <UpdateIamRole
              externalId={externalId}
              selectedAccount={editAccount}
              addOns={addOns}
              closeModal={onClose}
            />
          )}
        </SnackbarProvider>
      </DialogContent>
    </Dialog>
    </AccountDetailThemeProvider>
  );
};

export default EditAccountDialog;
